import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Anchor, Badge, Button, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconBellOff, IconChecklist, IconReportAnalytics } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { TodoInboxItem } from '../../lib/api';
import { formatWhen } from '../../pages/todos/todoLabels';

/** Nach so langer Abwesenheit vom Tab gilt die Rückkehr als neuer Besuch. */
const REVISIT_AFTER_MS = 15 * 60_000;
const RETRY_WHEN_BUSY_MS = 15_000;

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
  TODO_ASSIGNED: { label: 'Neu', color: 'blue' },
  TODO_DUE: { label: 'Fällig', color: 'orange' },
  TODO_UNSEEN: { label: 'Noch offen', color: 'yellow' },
  TODO_OVERDUE: { label: 'Überfällig', color: 'red' },
  TODO_ESCALATED: { label: 'Liegt', color: 'red' },
  TODO_DIGEST: { label: 'Tagesübersicht', color: 'grape' },
  ABSENCE_SUGGESTED: { label: 'Outlook', color: 'orange' },
  CALENDAR_INVITED: { label: 'Termin', color: 'blue' },
  CALENDAR_CHANGED: { label: 'Termin geändert', color: 'orange' },
  CALENDAR_CANCELLED: { label: 'Termin abgesagt', color: 'red' },
  CALENDAR_DECLINED: { label: 'Absage', color: 'red' },
};

/**
 * Nicht mitten in etwas hineinplatzen: kein Fenster über einem anderen Dialog (z. B. „Was ist
 * neu") und nicht, solange in einem Feld getippter Text steht — dieselbe Rücksicht wie beim
 * App-Update (lib/appUpdate).
 */
function isBusy(): boolean {
  // Nur der Inhalt zählt: Geschlossene Mantine-Dialoge lassen eine leere `…-root`-Hülle stehen.
  if (document.querySelector('.mantine-Modal-content, .mantine-Drawer-content')) return true;
  const el = document.activeElement as HTMLInputElement | HTMLTextAreaElement | HTMLElement | null;
  if (!el) return false;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value.trim().length > 0;
  return el.isContentEditable;
}

function pushMissing(): boolean {
  return typeof window !== 'undefined' && (!('Notification' in window) || Notification.permission !== 'granted');
}

/**
 * „Für dich" — beim Besuch im ServiceHub: neue, fällige, liegengebliebene Aufgaben, die
 * Tagesübersicht der Leitung und Termine aus dem Teamkalender (Einladung, geändert, abgesagt). Der Ersatz für Mail (Vorgabe: keine Mail), vor allem für alle ohne
 * Push-Gerät. Lebt aus denselben ungelesenen Glocken-Einträgen; „Verstanden" liest sie und zählt
 * bei eigenen Aufgaben als „angesehen" (sonst meldet sich nach 2 Std. die zweite Erinnerung).
 */
export function TodoInboxModal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<TodoInboxItem[]>([]);
  const [opened, setOpened] = useState(false);
  const [acking, setAcking] = useState(false);
  const dismissed = useRef(new Set<string>());
  const shownOnLoad = useRef(false);
  const hiddenSince = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);

  const inbox = useQuery({
    queryKey: ['todos', 'inbox'],
    queryFn: () => api.getTodoInbox(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const tryOpen = useCallback((list: TodoInboxItem[]) => {
    const fresh = list.filter((i) => !dismissed.current.has(i.id));
    if (fresh.length === 0) return;
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    if (isBusy()) {
      retryTimer.current = window.setTimeout(() => tryOpen(list), RETRY_WHEN_BUSY_MS);
      return;
    }
    setItems(fresh);
    setOpened(true);
  }, []);

  // Beim Laden der App (= Anmeldung, neuer Besuch) einmal.
  useEffect(() => {
    if (shownOnLoad.current || !inbox.data) return;
    shownOnLoad.current = true;
    tryOpen(inbox.data.items);
  }, [inbox.data, tryOpen]);

  const refetch = useRef(inbox.refetch);
  refetch.current = inbox.refetch;

  // Rückkehr nach längerer Pause gilt als neuer Besuch.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince.current = Date.now();
        return;
      }
      const since = hiddenSince.current;
      hiddenSince.current = null;
      if (since && Date.now() - since >= REVISIT_AFTER_MS) {
        void refetch.current().then((r) => r.data && tryOpen(r.data.items));
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [tryOpen]);

  useEffect(
    () => () => {
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    },
    [],
  );

  const ack = async (ids: string[]) => {
    if (ids.length === 0) return;
    setAcking(true);
    try {
      await api.ackTodoInbox(ids);
    } catch {
      // Nicht schlimm: Dann steht es beim nächsten Besuch noch einmal da.
    } finally {
      setAcking(false);
      void queryClient.invalidateQueries({ queryKey: ['todos'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    }
  };

  const close = (markRead: boolean) => {
    for (const i of items) dismissed.current.add(i.id);
    setOpened(false);
    if (markRead) void ack(items.map((i) => i.id));
  };

  const openItem = (item: TodoInboxItem) => {
    for (const i of items) dismissed.current.add(i.id);
    setOpened(false);
    void ack([item.id]);
    if (item.url) navigate(item.url);
  };

  const escalations = items.filter((i) => i.type === 'TODO_ESCALATED').length;

  return (
    <Modal
      opened={opened}
      onClose={() => close(false)}
      title={
        <Group gap="xs">
          <IconChecklist size={20} />
          <Text fw={600}>Für dich</Text>
        </Group>
      }
      centered
      size="md"
      zIndex={350}
    >
      <Stack gap="sm">
        {escalations > 0 && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} p="xs">
            {escalations === 1 ? 'Eine Aufgabe bleibt liegen.' : `${escalations} Aufgaben bleiben liegen.`}
          </Alert>
        )}
        {items.map((item) => {
          const badge = TYPE_BADGE[item.type];
          return (
            <Paper key={item.id} withBorder radius="md" p="sm" style={{ cursor: 'pointer' }} onClick={() => openItem(item)}>
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div style={{ minWidth: 0 }}>
                  <Group gap={6}>
                    {badge && (
                      <Badge size="xs" color={badge.color} variant="light">
                        {badge.label}
                      </Badge>
                    )}
                    <Text size="sm" fw={600}>
                      {item.title}
                    </Text>
                  </Group>
                  <Text size="sm" lineClamp={3}>
                    {item.body}
                  </Text>
                </div>
                <Group gap={4} wrap="nowrap">
                  {item.type === 'TODO_DIGEST' && <IconReportAnalytics size={16} />}
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {formatWhen(item.createdAt)}
                  </Text>
                </Group>
              </Group>
            </Paper>
          );
        })}
        {pushMissing() && (
          <Alert color="yellow" icon={<IconBellOff size={16} />} p="xs">
            Auf diesem Gerät sind Push-Benachrichtigungen aus — Aufgaben kommen dann erst beim nächsten Besuch an.{' '}
            <Anchor component={Link} to="/profile" size="sm" onClick={() => close(false)}>
              Im Profil einschalten
            </Anchor>
            {/iPhone|iPad/.test(navigator.userAgent) ? ' (iPhone: vorher über „Teilen → Zum Home-Bildschirm“ als App hinzufügen).' : '.'}
          </Alert>
        )}
        <Group justify="space-between">
          <Button variant="subtle" component={Link} to="/reminders" onClick={() => close(true)}>
            Alle Aufgaben
          </Button>
          <Button onClick={() => close(true)} loading={acking}>
            Verstanden
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
